import React from "react";
import {
  GlobalDateField,
  GlobalContactNumberField,
  GlobalEmailField,
  GlobalPasswordField,
  GlobalButton,
  GlobalSelectField,
  GlobalTextAreaField,
  GlobalTextField
} from "../../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import { GlobalPhotoPicker } from "../../../../global/forms/GlobalPhotoPicker";
import { useUserSectionContext } from "../UserSectionContext";
import { formatShiftOptionLabel } from "../userBuilderHelpers";

function IconUsers(props) {
  const base = { className: "ub-icon-svg", "aria-hidden": true };
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...base}
      {...props}
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

const UserCreationSection = () => {
  const {
    form,
    setField,
    submit,
    resetForm,
    isEdit,
    busy,
    showFormAlert,
    formAlertRef,
    fieldErrors,
    submitError,
    visibleRoles,
    maxDateOfJoining,
    saveAsActive,
    setSaveAsActive,
    photoFile,
    setPhotoFile,
    existingPhotoUrl,
    attendanceShifts,
    attendanceLocations,
  } = useUserSectionContext();

  const hasFieldErrors = Object.values(fieldErrors).some(Boolean);

  return (
    <div id="ub-users-create">
      <header className="ub-section__head">
        <div className="ub-section__icon-wrap ub-section__icon-wrap--users" aria-hidden>
          <IconUsers />
        </div>
        <div className="ub-section__head-text">
          <h2 id="users-heading" className="ub-section__title">
            Users
          </h2>
          <p className="ub-section__lead">
            New users get a system-assigned user code. Add role, contact, attendance
            shift/location, and profile fields below. Edit or remove rows from the
            directory.
          </p>
        </div>
      </header>

      <GlobalFormShell
        legend={isEdit ? "Edit user" : "Add user"}
        gridClassName="gfs-form-grid--3cols gfs-form-grid--user"
        showAlert={showFormAlert}
        alertText={
          submitError || (hasFieldErrors ? "Please fix the fields below." : "")
        }
        alertId="user-form-alert"
        formAlertRef={formAlertRef}
        actions={
          <>
            {isEdit ? (
              <GlobalButton
                disabled={busy}
                variant="ghost"
                onClick={resetForm}
              >
                Cancel edit
              </GlobalButton>
            ) : null}
            <GlobalButton
              disabled={busy}
              variant="primary"
              type="button"
              onClick={() => submit()}
            >
              {isEdit ? "Save user" : "Create user"}
            </GlobalButton>
          </>
        }
      >
        <GlobalPhotoPicker
          id="user-photo"
          label="Profile photo *"
          file={photoFile}
          existingUrl={existingPhotoUrl}
          onChange={setPhotoFile}
          error={fieldErrors.user_photo}
          baseAlertId={showFormAlert ? "user-form-alert" : undefined}
          optionalHint={
            isEdit
              ? "Replace the current photo from your device or camera. A photo is required."
              : "Add a face photo for the user directory (required). Click the avatar or use the buttons."
          }
        />

        <GlobalTextField
          id="user-name"
          label="Name"
          value={form.name}
          onChange={(v) => setField("name", v)}
          autoComplete="name"
          error={fieldErrors.name}
          nameTextRules
          baseAlertId={showFormAlert ? "user-form-alert" : undefined}
        />

        <GlobalEmailField
          id="user-email"
          label="Email"
          value={form.email}
          onChange={(v) => setField("email", v)}
          autoComplete="email"
          error={fieldErrors.email}
          emailFormatRules
          baseAlertId={showFormAlert ? "user-form-alert" : undefined}
        />

        <GlobalPasswordField
          id="user-password"
          label={isEdit ? "New password (optional)" : "Password"}
          value={form.password}
          onChange={(v) => setField("password", v)}
          placeholder={isEdit ? "Leave blank to keep current" : ""}
          error={fieldErrors.password}
          passwordPolicyRules
          baseAlertId={showFormAlert ? "user-form-alert" : undefined}
        />

        <GlobalSelectField
          id="user-role"
          label="Role"
          value={form.role_id}
          onChange={(v) => setField("role_id", v)}
          error={fieldErrors.role_id}
          options={visibleRoles.map((r) => ({
            value: r.id,
            label: r.role_name
          }))}
          baseAlertId={showFormAlert ? "user-form-alert" : undefined}
        />

        <GlobalContactNumberField
          id="user-phone"
          label="Contact number"
          value={form.contact_number}
          onChange={(v) => setField("contact_number", v)}
          error={fieldErrors.contact_number}
          baseAlertId={showFormAlert ? "user-form-alert" : undefined}
        />

        <GlobalDateField
          id="user-doj"
          label="Date of joining"
          value={form.date_of_joining}
          onChange={(v) => setField("date_of_joining", v)}
          max={maxDateOfJoining}
          error={fieldErrors.date_of_joining}
          baseAlertId={showFormAlert ? "user-form-alert" : undefined}
        />

        <GlobalSelectField
          id="user-attendance-shift"
          label="Attendance shift"
          value={form.attendance_shift_id}
          onChange={(v) => setField("attendance_shift_id", v)}
          options={(attendanceShifts || []).map((s) => ({
            value: s.id,
            label: formatShiftOptionLabel(s),
          }))}
          placeholderOption="None (auto by punch time)"
          baseAlertId={showFormAlert ? "user-form-alert" : undefined}
        />

        <GlobalSelectField
          id="user-attendance-location"
          label="Attendance location"
          value={form.attendance_location_id}
          onChange={(v) => setField("attendance_location_id", v)}
          options={(attendanceLocations || []).map((loc) => ({
            value: loc.id,
            label: loc.location_label ?? `Location #${loc.id}`,
          }))}
          placeholderOption="None (nearest allowed premises)"
          baseAlertId={showFormAlert ? "user-form-alert" : undefined}
        />

        <GlobalTextAreaField
          id="user-address"
          label="Address"
          value={form.address}
          onChange={(v) => setField("address", v)}
          rows={2}
          error={fieldErrors.address}
          classNames={{ fieldWrap: "gff-field gff-field--full" }}
          baseAlertId={showFormAlert ? "user-form-alert" : undefined}
        />

        <div
          className="ub-status-checkboxes gff-field--full"
          role="group"
          aria-label="Account status"
        >
          <label>
            <input
              type="checkbox"
              checked={saveAsActive}
              onChange={() => setSaveAsActive(true)}
            />
            Active
          </label>
          <label>
            <input
              type="checkbox"
              checked={!saveAsActive}
              onChange={() => setSaveAsActive(false)}
            />
            Inactive
          </label>
        </div>
      </GlobalFormShell>
    </div>
  );
};

export default UserCreationSection;
